import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { PresetArea, PresetObj } from "../../api-entities/PresetArea";
import { config } from "../../config";
import { makeAreaObj } from "../../entities/Area";
import { getUserAreas } from "../../store/action-creators/UserActionCreators";
import { ThunkDispatcher } from "../../store/actions/All";
import { makeApiUrl } from "../../utils/Api";
import AreasEditor, { AreaAction, AreaValidationProblem } from "../../components/AreasEditor";
import Col from "../../components/Common/Col";
import Row from "../../components/Common/Row";
import PageTitle from "../../components/Ui/PageTitle";
import TextInput from "../../components/Inputs/TextInput";
import { TRootState } from "../../store/reducers/RootReducer";

type UserAreaValidationResponse = {
	[P in keyof PresetObj]?: string[];
} &
	{ [P in keyof PresetObj["area"]]?: string[] } & { non_field_errors?: string[] } & {
		[key: string]: string[];
	};

const saveUserArea = async (preset: PresetArea, token?: string): Promise<PresetArea> => {
	const method = preset.id ? "PUT" : "POST";
	const url = makeApiUrl(`${config.apiUrl}user-area/${preset.id || ""}`, token);
	const response = await fetch(url, {
		method,
		headers: {
			"Content-Type": "application/json"
		},
		credentials: "include",
		body: JSON.stringify(preset)
	});

	if (response.ok) {
		return response.json();
	} else {
		if (response.status === 400) {
			return Promise.reject(await response.json());
		} else {
			throw new Error(`Save area failed with status ${response.status}`);
		}
	}
};

const deleteUserArea = async (id: number | undefined, token?: string): Promise<void> => {
	if (id === undefined) {
		return;
	}

	const url = makeApiUrl(`${config.apiUrl}user-area/${id}`, token);
	const response = await fetch(url, {
		method: "DELETE",
		credentials: "include",
	});

	if (response.ok) {
		return;
	} else {
		throw new Error(`Delete area failed with status ${response.status}`);
	}
};

const presetObjToApiFormat = (preset: PresetObj) => ({
	id: preset.id,
	name: preset.name,
	north: preset.area.north.toString(),
	west: preset.area.west.toString(),
	south: preset.area.south.toString(),
	east: preset.area.east.toString()
});

const convertValidationResponse = (validationResponse: UserAreaValidationResponse) =>
	Object.keys(validationResponse)
		.map(k =>
			validationResponse[k].map(
				m =>
					({
						field: k,
						message: m
					} as AreaValidationProblem)
			)
		)
		.reduce((xs, ys) => [...xs, ...ys], []);

const projectArea = (p: PresetObj) => p.area;
const doNothing = () => { };

/**
 * A gatekeeper for the areas library page which ensures that
 * the user is logged in
 */
const AreaLibraryPage = () => {
	const token = useSelector<TRootState, string | undefined>(state => state.user.token);

	const dispatch: ThunkDispatcher = useDispatch();
	const presets = useSelector<TRootState, PresetObj[] | undefined>(state => state.user.areaPresets);

	const [workingPresets, setWorkingPresets] = React.useState<PresetObj[] | undefined>();
	const [validationProblems, setValidationProblems] = React.useState<AreaValidationProblem[][]>();
	const [hasChanges, setHasChanges] = React.useState<boolean[]>();

	const setWorkingPreset = React.useCallback(
		(index: number, preset: PresetObj, matchesRemote: boolean) => {
			if (!workingPresets || !hasChanges) {
				return;
			}

			const newPresets = workingPresets.slice();
			newPresets[index] = preset;
			setWorkingPresets(newPresets);

			const newHasChanges = hasChanges.slice();
			newHasChanges[index] = !matchesRemote;
			setHasChanges(newHasChanges);
		},
		[setWorkingPresets, workingPresets, hasChanges, setHasChanges]
	);

	// Create working copy from store if one doesn't already exist
	React.useEffect(() => {
		if (workingPresets !== undefined) {
			return;
		}

		if (presets === undefined) {
			dispatch(getUserAreas);
			return;
		}

		setWorkingPresets(presets);
		setValidationProblems(presets.map(_ => []));
		setHasChanges(presets.map(_ => false));
	}, [
		presets,
		workingPresets,
		setWorkingPresets,
		setValidationProblems,
		setHasChanges,
		dispatch
	]);

	// Unmount effect, refresh store from server
	React.useEffect(() => {
		return () => {
			dispatch(getUserAreas);
		};
	}, [dispatch]);

	const pushPresetToRemote = React.useCallback(
		(preset: PresetObj, index: number) => {
			if (!validationProblems) {
				throw new Error("Call to push presets while component not loaded");
			}

			const updateValidationProblems = (problems: AreaValidationProblem[]) => {
				const newValidationProblems = validationProblems.slice();
				newValidationProblems[index] = problems;
				setValidationProblems(newValidationProblems);
			};

			saveUserArea(presetObjToApiFormat(preset), token)
				.then(response => {
					setWorkingPreset(index, makeAreaObj(response), true);
					updateValidationProblems([]);
				})
				.catch((validationResponse: UserAreaValidationResponse) => {
					// tslint:disable-next-line: no-console
					updateValidationProblems(convertValidationResponse(validationResponse));
				});
		},
		[validationProblems, setValidationProblems, setWorkingPreset, token]
	);

	const handleAction = React.useCallback(
		async (action: AreaAction) => {
			if (
				workingPresets === undefined ||
				validationProblems === undefined ||
				hasChanges === undefined
			) {
				throw new Error("Action handled while component not loaded");
			}

			switch (action.type) {
				case "create": {
					const newPreset = { name: "", area: action.area };
					setWorkingPresets([...workingPresets, newPreset]);
					setValidationProblems([...validationProblems, []]);
					setHasChanges([...hasChanges, true]);
					pushPresetToRemote(newPreset, workingPresets.length);
					break;
				}
				case "update": {
					const newPreset = { ...workingPresets[action.index], area: action.area };
					setWorkingPreset(action.index, newPreset, false);
					pushPresetToRemote(newPreset, action.index);
					break;
				}
				case "delete": {
					deleteUserArea(workingPresets[action.index].id, token)
						.then(() => {
							const newPresets = workingPresets.slice();
							newPresets.splice(action.index, 1);
							setWorkingPresets(newPresets);
							const newValidationProblems = validationProblems.slice();
							newValidationProblems.splice(action.index, 1);
							setValidationProblems(newValidationProblems);
							const newHasChanges = hasChanges.slice();
							newHasChanges.splice(action.index, 1);
							setHasChanges(newHasChanges);
						})
						.catch(() => {
							throw new Error("Delete area failed");
						});
					break;
				}
			}
		},
		[
			setWorkingPresets,
			workingPresets,
			validationProblems,
			setValidationProblems,
			pushPresetToRemote,
			token,
			setWorkingPreset,
			hasChanges,
			setHasChanges
		]
	);

	const setName = React.useCallback(
		(index: number, name: string) => {
			if (workingPresets === undefined) {
				throw new Error("Name updated while component not loaded");
			}

			const newPreset = { ...workingPresets[index], name };
			setWorkingPreset(index, newPreset, false);
		},
		[workingPresets, setWorkingPreset]
	);

	const savePreset = React.useCallback(
		(index: number) => {
			if (workingPresets === undefined) {
				throw new Error("Name updated while component not loaded");
			}

			pushPresetToRemote(workingPresets[index], index);
		},
		[workingPresets, pushPresetToRemote]
	);

	const renderAdditionalField = React.useCallback(
		(index: number | null) => {
			if (workingPresets === undefined || validationProblems === undefined) {
				throw new Error("Additional fields rendered while component not loaded");
			}

			return (
				index !== null && (
					<div>
						<TextInput
							label="Name"
							value={workingPresets[index].name}
							onChange={value => setName(index, value)}
							onBlur={() => savePreset(index)}
						></TextInput>
					</div>
				)
			);
		},
		[workingPresets, setName, savePreset, validationProblems]
	);

	return workingPresets ? (
		<>
			<Row>
				<Col md={6}>
					<PageTitle title="Area preset library" />
					<p>
						You can use the tool below to draw and save areas that you wish to apply to multiple
						real-time product configurations.
						</p>

					<p>
						Once you have drawn an area and named it, this will be saved and available to you when
						configuring products.
						</p>

					<p>
						When configuring real-time products, the area you have saved here will snap to the
						chosen resolution of that configuration (it will expand if a coarser resolution is
						selected).
						</p>
				</Col>
			</Row>
			<AreasEditor<PresetObj>
				{...{
					areaEntities: workingPresets,
					projectArea,
					handleAction,
					validationProblems
				}}
				onBlur={doNothing}
				needsFocus={false}
				acknowledgeFocusRequest={doNothing}
			>
				{renderAdditionalField}
			</AreasEditor>
		</>
	) : null;
};

export default AreaLibraryPage;
